/**
 * #1158 — Kontakte (zentrales Adressbuch)
 *
 * Person-zentrierte Verwaltung: 1 Row = 1 Person, mehrere Mails + Telefone,
 * optionale Kunden-Zuordnung (n:1), Foto, Notizen.
 *
 * @page /kontakte
 * @roles admin, manager, controlling, customer_success
 * @api /api/contacts, /api/contacts/:id, /api/contacts/:id/emails|phones|foto
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PageHeader, Button, Modal } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { Search, Plus, Pencil, Trash2, Mail, Phone, Building2, User, X, Star, Upload, ImageOff, Users, UserPlus, Sparkles, AlertCircle, Check } from 'lucide-react';

function api(path, opts = {}) {
  const isMultipart = opts.body instanceof FormData;
  return fetch(path, {
    credentials: 'include',
    headers: isMultipart ? {} : { 'Content-Type': 'application/json' },
    ...opts,
    body: isMultipart ? opts.body : (opts.body ? JSON.stringify(opts.body) : undefined),
  }).then(async (r) => {
    if (!r.ok) {
      const txt = await r.text();
      let parsed; try { parsed = JSON.parse(txt); } catch { parsed = { error: txt }; }
      throw new Error(parsed.error || `HTTP ${r.status}`);
    }
    return r.json();
  });
}

function Avatar({ contact, size = 36 }) {
  const [errored, setErrored] = useState(false);
  const initials = (() => {
    const name = (contact.anzeige_name || `${contact.vorname || ''} ${contact.nachname || ''}`).trim();
    if (!name) return '?';
    const parts = name.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
  })();
  if (contact.foto_url && !errored) {
    return (
      <img
        src={contact.foto_url}
        onError={() => setErrored(true)}
        style={{ width: size, height: size }}
        className="rounded-full object-cover border border-line"
        alt=""
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.35 }}
      className="rounded-full bg-[#338edc]/15 text-[#338edc] flex items-center justify-center font-semibold shrink-0"
    >
      {initials}
    </div>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm, danger }) {
  const { t } = useLanguage();
  if (!open) return null;
  return (
    <Modal open={open} onClose={onCancel} title={title} size="sm" footer={
      <>
        <Button variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{t('common.confirm')}</Button>
      </>
    }>
      <p className="text-sm text-content">{message}</p>
    </Modal>
  );
}

function EditorModal({ open, onClose, contact, kunden, onSaved, t }) {
  const [form, setForm] = useState(() => ({
    vorname: '', nachname: '', anzeige_name: '', firma_freitext: '',
    kunde_id: '', notizen: '', status: 'bestaetigt', notify_on_case: true,
    emails: [{ email: '', label: '', is_primary: true }],
    phones: [],
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fotoFile, setFotoFile] = useState(null);
  const fotoInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    setErr('');
    setFotoFile(null);
    setPreviewUrl(null);
    if (contact) {
      setForm({
        vorname: contact.vorname || '',
        nachname: contact.nachname || '',
        anzeige_name: contact.anzeige_name || '',
        firma_freitext: contact.firma_freitext || '',
        kunde_id: contact.kunde_id || '',
        notizen: contact.notizen || '',
        status: contact.status || 'bestaetigt',
        notify_on_case: contact.notify_on_case !== false,
        emails: contact.emails && contact.emails.length
          ? contact.emails.map((e) => ({ id: e.id, email: e.email, label: e.label || '', is_primary: !!e.is_primary }))
          : [{ email: '', label: '', is_primary: true }],
        phones: (contact.phones || []).map((p) => ({ id: p.id, phone: p.phone, label: p.label || '', is_primary: !!p.is_primary })),
      });
    } else {
      setForm({
        vorname: '', nachname: '', anzeige_name: '', firma_freitext: '',
        kunde_id: '', notizen: '', status: 'bestaetigt', notify_on_case: true,
        emails: [{ email: '', label: '', is_primary: true }],
        phones: [],
      });
    }
  }, [open, contact]);

  const onPickFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setErr('');
    if (!form.vorname && !form.nachname && !form.anzeige_name && !form.firma_freitext) {
      setErr(t('contacts.editor.errorNameRequired'));
      return;
    }
    setSaving(true);
    try {
      const cleanEmails = form.emails.filter((e) => e.email && e.email.trim());
      const cleanPhones = form.phones.filter((p) => p.phone && p.phone.trim());

      const payload = {
        vorname: form.vorname.trim() || null,
        nachname: form.nachname.trim() || null,
        anzeige_name: form.anzeige_name.trim() || null,
        firma_freitext: form.firma_freitext.trim() || null,
        kunde_id: form.kunde_id ? Number(form.kunde_id) : null,
        notizen: form.notizen.trim() || null,
        status: form.status,
        notify_on_case: form.notify_on_case,
        emails: cleanEmails,
        phones: cleanPhones,
      };

      let savedId;
      if (contact?.id) {
        await api(`/api/contacts/${contact.id}`, { method: 'PUT', body: payload });
        savedId = contact.id;
        // Sync emails: delete removed, upsert new
        const existingEmailIds = (contact.emails || []).map((e) => e.id);
        const keepEmailIds = cleanEmails.filter((e) => e.id).map((e) => e.id);
        for (const eid of existingEmailIds) {
          if (!keepEmailIds.includes(eid)) {
            await api(`/api/contacts/${savedId}/emails/${eid}`, { method: 'DELETE' });
          }
        }
        for (const e of cleanEmails) {
          if (e.id) {
            await api(`/api/contacts/${savedId}/emails/${e.id}`, {
              method: 'PUT', body: { email: e.email.trim(), label: e.label || null, is_primary: !!e.is_primary },
            });
          } else {
            await api(`/api/contacts/${savedId}/emails`, {
              method: 'POST', body: { email: e.email.trim(), label: e.label || null, is_primary: !!e.is_primary },
            });
          }
        }
        const existingPhoneIds = (contact.phones || []).map((p) => p.id);
        const keepPhoneIds = cleanPhones.filter((p) => p.id).map((p) => p.id);
        for (const pid of existingPhoneIds) {
          if (!keepPhoneIds.includes(pid)) {
            await api(`/api/contacts/${savedId}/phones/${pid}`, { method: 'DELETE' });
          }
        }
        for (const p of cleanPhones) {
          if (p.id) {
            await api(`/api/contacts/${savedId}/phones/${p.id}`, {
              method: 'PUT', body: { phone: p.phone.trim(), label: p.label || null, is_primary: !!p.is_primary },
            });
          } else {
            await api(`/api/contacts/${savedId}/phones`, {
              method: 'POST', body: { phone: p.phone.trim(), label: p.label || null, is_primary: !!p.is_primary },
            });
          }
        }
      } else {
        const res = await api('/api/contacts', { method: 'POST', body: payload });
        savedId = res.contact.id;
      }

      if (fotoFile) {
        const fd = new FormData();
        fd.append('file', fotoFile);
        await api(`/api/contacts/${savedId}/foto`, { method: 'POST', body: fd });
      }

      onSaved(savedId);
    } catch (e) {
      setErr(e.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const setEmail = (i, k, v) => {
    setForm((f) => {
      const emails = [...f.emails];
      if (k === 'is_primary' && v) emails.forEach((e, idx) => { e.is_primary = idx === i; });
      else emails[i] = { ...emails[i], [k]: v };
      return { ...f, emails };
    });
  };
  const addEmail = () => setForm((f) => ({ ...f, emails: [...f.emails, { email: '', label: '', is_primary: f.emails.length === 0 }] }));
  const rmEmail = (i) => setForm((f) => ({ ...f, emails: f.emails.filter((_, idx) => idx !== i) }));

  const setPhone = (i, k, v) => {
    setForm((f) => {
      const phones = [...f.phones];
      if (k === 'is_primary' && v) phones.forEach((p, idx) => { p.is_primary = idx === i; });
      else phones[i] = { ...phones[i], [k]: v };
      return { ...f, phones };
    });
  };
  const addPhone = () => setForm((f) => ({ ...f, phones: [...f.phones, { phone: '', label: '', is_primary: f.phones.length === 0 }] }));
  const rmPhone = (i) => setForm((f) => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));

  const inputCls = 'w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/30';

  const currentPreview = previewUrl || (contact?.foto_url ? `${contact.foto_url}?t=${contact.updated_at || ''}` : null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact ? t('contacts.editor.titleEdit') : t('contacts.editor.titleNew')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {err && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          {err}
        </div>
      )}

      <div className="grid grid-cols-[120px_1fr] gap-5">
        <div className="flex flex-col items-center gap-2">
          {currentPreview ? (
            <img src={currentPreview} alt="" className="w-[100px] h-[100px] rounded-full object-cover border border-line" />
          ) : (
            <div className="w-[100px] h-[100px] rounded-full bg-surface-inset border border-line flex items-center justify-center text-[var(--text-tertiary)]">
              <ImageOff size={28} />
            </div>
          )}
          <input ref={fotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickFoto} className="hidden" />
          <button
            type="button"
            onClick={() => fotoInputRef.current?.click()}
            className="text-xs text-[#338edc] hover:underline flex items-center gap-1"
          >
            <Upload size={12} /> {t('contacts.editor.uploadPhoto')}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('contacts.editor.vorname')}
            </label>
            <input className={inputCls} value={form.vorname} onChange={(e) => setForm((f) => ({ ...f, vorname: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('contacts.editor.nachname')}
            </label>
            <input className={inputCls} value={form.nachname} onChange={(e) => setForm((f) => ({ ...f, nachname: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('contacts.editor.anzeigeName')}
            </label>
            <input className={inputCls} value={form.anzeige_name}
              onChange={(e) => setForm((f) => ({ ...f, anzeige_name: e.target.value }))}
              placeholder={t('contacts.editor.anzeigeNamePlaceholder')} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('contacts.editor.firma')}
            </label>
            <input className={inputCls} value={form.firma_freitext}
              onChange={(e) => setForm((f) => ({ ...f, firma_freitext: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('contacts.editor.kunde')}
            </label>
            <select className={inputCls} value={form.kunde_id}
              onChange={(e) => setForm((f) => ({ ...f, kunde_id: e.target.value }))}>
              <option value="">{t('contacts.editor.noKunde')}</option>
              {kunden.map((k) => (
                <option key={k.id} value={k.id}>{k.name || `#${k.id}`}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-content flex items-center gap-2">
            <Mail size={14} /> {t('contacts.editor.emails')}
          </h3>
          <button type="button" onClick={addEmail} className="text-xs text-[#338edc] hover:underline flex items-center gap-1">
            <Plus size={12} /> {t('contacts.editor.addEmail')}
          </button>
        </div>
        <div className="space-y-2">
          {form.emails.map((e, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_auto_auto] gap-2 items-center">
              <input className={inputCls} type="email" value={e.email} placeholder="email@example.com"
                onChange={(ev) => setEmail(i, 'email', ev.target.value)} />
              <input className={inputCls} value={e.label} placeholder={t('contacts.editor.labelPlaceholder')}
                onChange={(ev) => setEmail(i, 'label', ev.target.value)} />
              <button
                type="button"
                onClick={() => setEmail(i, 'is_primary', !e.is_primary)}
                title={t('contacts.editor.setPrimary')}
                className={`p-2 rounded-lg ${e.is_primary ? 'text-amber-400' : 'text-[var(--text-tertiary)] hover:text-content'}`}
              >
                <Star size={16} fill={e.is_primary ? 'currentColor' : 'none'} />
              </button>
              <button type="button" onClick={() => rmEmail(i)} className="p-2 rounded-lg text-[var(--text-tertiary)] hover:text-red-400">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-content flex items-center gap-2">
            <Phone size={14} /> {t('contacts.editor.phones')}
          </h3>
          <button type="button" onClick={addPhone} className="text-xs text-[#338edc] hover:underline flex items-center gap-1">
            <Plus size={12} /> {t('contacts.editor.addPhone')}
          </button>
        </div>
        <div className="space-y-2">
          {form.phones.length === 0 && (
            <p className="text-xs text-[var(--text-tertiary)] italic">{t('contacts.editor.noPhones')}</p>
          )}
          {form.phones.map((p, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_auto_auto] gap-2 items-center">
              <input className={inputCls} value={p.phone} placeholder="+43 123 456 789"
                onChange={(ev) => setPhone(i, 'phone', ev.target.value)} />
              <input className={inputCls} value={p.label} placeholder={t('contacts.editor.labelPlaceholder')}
                onChange={(ev) => setPhone(i, 'label', ev.target.value)} />
              <button
                type="button"
                onClick={() => setPhone(i, 'is_primary', !p.is_primary)}
                title={t('contacts.editor.setPrimary')}
                className={`p-2 rounded-lg ${p.is_primary ? 'text-amber-400' : 'text-[var(--text-tertiary)] hover:text-content'}`}
              >
                <Star size={16} fill={p.is_primary ? 'currentColor' : 'none'} />
              </button>
              <button type="button" onClick={() => rmPhone(i)} className="p-2 rounded-lg text-[var(--text-tertiary)] hover:text-red-400">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <label className="block text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-1">
          {t('contacts.editor.notizen')}
        </label>
        <textarea
          className={`${inputCls} min-h-[80px] font-sans`}
          value={form.notizen}
          onChange={(e) => setForm((f) => ({ ...f, notizen: e.target.value }))}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={form.notify_on_case}
            onChange={(e) => setForm((f) => ({ ...f, notify_on_case: e.target.checked }))} />
          {t('contacts.editor.notifyOnCase')}
        </label>
        <label className="flex items-center gap-2 text-sm text-content">
          <span className="text-xs text-[var(--text-secondary)]">{t('contacts.editor.status')}:</span>
          <select className={inputCls.replace('w-full', 'w-auto')} value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
            <option value="bestaetigt">{t('contacts.status.bestaetigt')}</option>
            <option value="vorgeschlagen">{t('contacts.status.vorgeschlagen')}</option>
          </select>
        </label>
      </div>
    </Modal>
  );
}

export default function Contacts() {
  const { t } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [query, setQuery] = useState('');
  const [filterKunde, setFilterKunde] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (filterKunde) params.set('kunde_id', filterKunde);
      if (filterStatus) params.set('status', filterStatus);
      if (filterSource) params.set('source', filterSource);
      const data = await api(`/api/contacts?${params}`);
      setContacts(data.contacts || []);
    } catch (e) {
      setErr(e.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [query, filterKunde, filterStatus, filterSource, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/api/kunden').then((d) => {
      setKunden(Array.isArray(d) ? d.filter((k) => k.aktiv !== false) : []);
    }).catch(() => setKunden([]));
  }, []);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (id) => {
    try {
      const d = await api(`/api/contacts/${id}`);
      setEditing(d.contact);
      setEditorOpen(true);
    } catch (e) {
      setErr(e.message);
    }
  };
  const onSaved = () => { setEditorOpen(false); setEditing(null); load(); };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api(`/api/contacts/${deleteConfirm.id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  // #1159 Teil B — Vorschläge-Aktionen
  const doBestaetigen = async (c) => {
    try {
      await api(`/api/contacts/${c.id}/bestaetigen`, { method: 'POST' });
      load();
    } catch (e) {
      setErr(e.message);
    }
  };
  const doVerwerfen = async (c) => {
    try {
      await api(`/api/contacts/${c.id}/verwerfen`, { method: 'POST' });
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const stats = useMemo(() => ({
    total: contacts.length,
    manual: contacts.filter((c) => c.source === 'manuell').length,
    auto: contacts.filter((c) => c.source === 'auto').length,
    vorgeschlagen: contacts.filter((c) => c.status === 'vorgeschlagen').length,
  }), [contacts]);

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('contacts.title')}
        subtitle={t('contacts.subtitle')}
        actions={
          <Button variant="primary" onClick={openNew}>
            <Plus size={16} /> {t('contacts.new')}
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label={t('contacts.stats.total')} value={stats.total}
          Icon={Users} iconColor="#338edc" iconTint="#EBF5FD" />
        <StatTile label={t('contacts.stats.manual')} value={stats.manual}
          Icon={UserPlus} iconColor="#10B981" iconTint="#D1FAE5" />
        <StatTile label={t('contacts.stats.auto')} value={stats.auto}
          Icon={Sparkles} iconColor="#6366F1" iconTint="#E0E7FF" />
        <StatTile label={t('contacts.stats.vorgeschlagen')} value={stats.vorgeschlagen}
          Icon={AlertCircle} iconColor="#F59E0B" iconTint="#FEF3C7"
          active={filterStatus === 'vorgeschlagen'}
          onClick={() => setFilterStatus(filterStatus === 'vorgeschlagen' ? '' : 'vorgeschlagen')} />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('contacts.searchPlaceholder')}
            className="w-full bg-surface-card border border-line rounded-lg pl-9 pr-3 py-2 text-sm text-content focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/30"
          />
        </div>
        <select className="bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content"
          value={filterKunde} onChange={(e) => setFilterKunde(e.target.value)}>
          <option value="">{t('contacts.filter.allKunden')}</option>
          <option value="null">{t('contacts.filter.noKunde')}</option>
          {kunden.map((k) => (
            <option key={k.id} value={k.id}>{k.name || `#${k.id}`}</option>
          ))}
        </select>
        <select className="bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content"
          value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">{t('contacts.filter.allStatus')}</option>
          <option value="bestaetigt">{t('contacts.status.bestaetigt')}</option>
          <option value="vorgeschlagen">{t('contacts.status.vorgeschlagen')}</option>
        </select>
        <select className="bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content"
          value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
          <option value="">{t('contacts.filter.allSource')}</option>
          <option value="manuell">{t('contacts.source.manuell')}</option>
          <option value="auto">{t('contacts.source.auto')}</option>
        </select>
      </div>

      {err && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          {err}
        </div>
      )}

      <div className="mt-5 bg-surface-card border border-line rounded-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              <th className="text-left px-4 py-3 font-medium">{t('contacts.col.contact')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('contacts.col.kunde')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('contacts.col.email')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('contacts.col.phone')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('contacts.col.status')}</th>
              <th className="text-right px-4 py-3 font-medium">{t('contacts.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('common.loading')}</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('contacts.empty')}</td></tr>
            ) : contacts.map((c) => (
              <tr key={c.id} className="border-b border-line last:border-0 hover:bg-surface-hover transition-colors">
                <td className="px-4 py-3">
                  <button onClick={() => openEdit(c.id)} className="flex items-center gap-3 text-left">
                    <Avatar contact={c} />
                    <div>
                      <div className="text-sm font-medium text-content">
                        {c.anzeige_name || `${c.vorname || ''} ${c.nachname || ''}`.trim() || c.firma_freitext || `#${c.id}`}
                      </div>
                      {c.firma_freitext && (
                        <div className="text-xs text-[var(--text-tertiary)]">{c.firma_freitext}</div>
                      )}
                    </div>
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-content">
                  {c.kunde_firma ? (
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Building2 size={12} className="text-[var(--text-tertiary)]" />
                      {c.kunde_firma}
                    </span>
                  ) : (
                    <span className="text-xs text-[var(--text-tertiary)] italic">{t('contacts.noKunde')}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-content">
                  {c.primary_email ? (
                    <div className="flex items-center gap-1">
                      <a href={`mailto:${c.primary_email}`} className="text-[#338edc] hover:underline truncate max-w-[200px] inline-block">{c.primary_email}</a>
                      {c.email_count > 1 && <span className="text-xs text-[var(--text-tertiary)]">+{c.email_count - 1}</span>}
                    </div>
                  ) : <span className="text-xs text-[var(--text-tertiary)]">—</span>}
                </td>
                <td className="px-4 py-3 text-sm text-content">
                  {c.primary_phone ? (
                    <div className="flex items-center gap-1">
                      <a href={`tel:${c.primary_phone}`} className="text-[#338edc] hover:underline">{c.primary_phone}</a>
                      {c.phone_count > 1 && <span className="text-xs text-[var(--text-tertiary)]">+{c.phone_count - 1}</span>}
                    </div>
                  ) : <span className="text-xs text-[var(--text-tertiary)]">—</span>}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={c.status} source={c.source} t={t} />
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {c.status === 'vorgeschlagen' && (
                    <>
                      <button onClick={() => doBestaetigen(c)} className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-emerald-500 hover:bg-surface-hover" title={t('contacts.action.confirm')}>
                        <Check size={15} />
                      </button>
                      <button onClick={() => doVerwerfen(c)} className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-amber-500 hover:bg-surface-hover" title={t('contacts.action.discard')}>
                        <X size={15} />
                      </button>
                    </>
                  )}
                  <button onClick={() => openEdit(c.id)} className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-surface-hover" title={t('common.edit')}>
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => setDeleteConfirm(c)} className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-red-400 hover:bg-surface-hover" title={t('common.delete')}>
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditorModal
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        contact={editing}
        kunden={kunden}
        onSaved={onSaved}
        t={t}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        title={t('contacts.delete.title')}
        message={t('contacts.delete.message', { name: deleteConfirm?.anzeige_name || '' })}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={doDelete}
        danger
      />
    </div>
  );
}

function StatTile({ label, value, Icon, iconColor, iconTint, onClick, active }) {
  const interactive = typeof onClick === 'function';
  const base = 'bg-[var(--bg-card,#fff)] border rounded-xl p-5 text-left transition-colors';
  const borderCls = active
    ? 'border-[#338edc] ring-1 ring-[#338edc]/30'
    : 'border-[var(--border-default,#E5E7EB)]';
  const hoverCls = interactive ? 'hover:border-[#338edc]/60 cursor-pointer' : '';
  const Tag = interactive ? 'button' : 'div';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      className={`${base} ${borderCls} ${hoverCls} w-full`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{label}</span>
        {Icon && (
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: iconTint }}
          >
            <Icon size={18} style={{ color: iconColor }} />
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
    </Tag>
  );
}

function StatusPill({ status, source, t }) {
  const isVorgeschlagen = status === 'vorgeschlagen';
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full w-fit ${
        isVorgeschlagen ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'
      }`}>
        {t(`contacts.status.${status}`)}
      </span>
      <span className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">
        {t(`contacts.source.${source}`)}
      </span>
    </div>
  );
}
